import { useEffect, useRef, useState } from 'react';
import { HexColorInput } from 'react-colorful';
import { useTranslation } from 'react-i18next';

import { Form, Formik } from 'formik';

import { SingleSelect } from '@/common';
import { UploadButton } from '@/components/common';
import { Button, ColorPicker, Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { UPLOAD_PICKER_TYPES } from '@/constants/UploadFileConstants';
import { getAssetBlobClient, getAssetUrl, tryUploadAsset } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import i18n from '@/i18n';
import { ArrowDownHeavyIcon, ArrowUpHeavyIcon, TrashIcon } from '@/resources';
import {
  useCreateOrganizationLogoSasTokenMutation,
  useGetOrganizationBrandingQuery,
  useUpdateOrganizationBrandingMutation
} from '@/services';
import { HexColor, OrganizationBranding, SelectOptionProps } from '@/types';

type OrganizationBrandingTabProps = {
  organizationId: number;
  language?: string;
};

type ColorFieldName =
  | 'headerBackgroundColor'
  | 'highlightColor'
  | 'linkColor'
  | 'footerColor'
  | 'footerBackgroundColor';

type FormValues = {
  logoUrl: string;
  fontFamily: string;
  headerBackgroundColor: string;
  highlightColor: string;
  linkColor: string;
  footerColor: string;
  footerBackgroundColor: string;
};

const SAMPLE_USER_NAME = 'Test User';
const SAMPLE_INVITATION_URL = 'https://app.sentoa.com/invitation/sample-token';

const DEFAULT_BRANDING: FormValues = {
  logoUrl: 'https://sentoa.cloud/wp-content/uploads/2024/10/6741952.png',
  fontFamily: 'Arial, sans-serif',
  headerBackgroundColor: '#002c5a',
  highlightColor: '#0056a3',
  linkColor: '#007bff',
  footerColor: '#888888',
  footerBackgroundColor: '#edf2fc'
};

const FONT_FAMILY_OPTIONS: SelectOptionProps[] = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, sans-serif', label: 'Tahoma' },
  { value: '"Trebuchet MS", sans-serif', label: 'Trebuchet MS' },
  { value: '"Courier New", monospace', label: 'Courier New' }
];

const getFontFamilyOption = (fontFamily: string) =>
  FONT_FAMILY_OPTIONS.find((option) => option.value === fontFamily) ?? null;

type ColorFieldPickerProps = {
  label: string;
  value: string;
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
  onChange: (value: string) => void;
};

const ColorFieldPicker = ({ label, value, isOpen, onToggle, onChange }: ColorFieldPickerProps) => {
  const color = value as HexColor;

  const handleChange = (newColor: string) => {
    onChange(newColor.slice(0, 7).toLowerCase());
  };

  return (
    <div className='flex flex-col gap-2'>
      <Label text={label} type={LABEL_TYPE.PARAGRAPH2} />
      <div
        className='flex h-10 w-full cursor-pointer items-center justify-between rounded-xl border border-secondary-blue-gray p-2'
        onClick={() => onToggle(!isOpen)}>
        <div style={{ backgroundColor: color }} className='size-5 rounded-full border-2' />
        {isOpen ? (
          <ArrowUpHeavyIcon className='size-3 stroke-[0.5] text-black' />
        ) : (
          <ArrowDownHeavyIcon className='size-3 stroke-[0.5] text-black' />
        )}
      </div>
      <div className='relative'>
        {isOpen && (
          <div className='absolute left-0 z-10 mt-2 rounded-md p-2'>
            <ColorPicker
              isOpen={isOpen}
              setIsOpen={onToggle}
              color={color}
              onChange={handleChange}
              showColorPreview={false}
            />
            <HexColorInput
              color={color}
              onChange={handleChange}
              className='absolute -bottom-4 left-0 w-[150px] rounded-md border-2 py-1 text-center outline-none'
              data-prevent-click-outside={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export const OrganizationBrandingTab = ({
  organizationId,
  language
}: OrganizationBrandingTabProps) => {
  const [t] = useTranslation('common');
  const previewT = i18n.getFixedT(language || i18n.language, 'common') as (key: string) => string;
  const [openPicker, setOpenPicker] = useState<ColorFieldName | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const logoPreviewUrl = useRef<string | null>(null);

  const revokeLogoPreview = () => {
    if (logoPreviewUrl.current) {
      URL.revokeObjectURL(logoPreviewUrl.current);
      logoPreviewUrl.current = null;
    }
  };

  useEffect(() => revokeLogoPreview, []);

  const { data, isLoading, refetch } = useGetOrganizationBrandingQuery(organizationId);

  const { mutateAsync: createLogoSasToken } = useCreateOrganizationLogoSasTokenMutation({
    onSuccess: () => undefined,
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const { mutateAsync: updateBranding, isLoading: isSaving } =
    useUpdateOrganizationBrandingMutation(queryClient, {
      onSuccess: () => {
        showToast(ToastType.SUCCESS, t('brandingUpdated'));
        setLogoFile(null);
        refetch();
      },
      onError: (error) => {
        showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      }
    });

  if (isLoading) {
    return <Spinner />;
  }

  const branding = data?.data;

  const initialValues: FormValues = {
    logoUrl: branding?.logoUrl ?? DEFAULT_BRANDING.logoUrl,
    fontFamily: branding?.fontFamily ?? DEFAULT_BRANDING.fontFamily,
    headerBackgroundColor:
      branding?.headerBackgroundColor ?? DEFAULT_BRANDING.headerBackgroundColor,
    highlightColor: branding?.highlightColor ?? DEFAULT_BRANDING.highlightColor,
    linkColor: branding?.linkColor ?? DEFAULT_BRANDING.linkColor,
    footerColor: branding?.footerColor ?? DEFAULT_BRANDING.footerColor,
    footerBackgroundColor: branding?.footerBackgroundColor ?? DEFAULT_BRANDING.footerBackgroundColor
  };

  const isSavedAsDefault = (Object.keys(DEFAULT_BRANDING) as (keyof FormValues)[]).every(
    (key) => initialValues[key] === DEFAULT_BRANDING[key]
  );

  const uploadLogo = async (file: File) => {
    try {
      const extension = `.${file.name.split('.').pop()}`;
      const sasTokenResponse = await createLogoSasToken({ organizationId, extension });
      const blobClient = getAssetBlobClient(sasTokenResponse);
      if (!blobClient) {
        return null;
      }

      const uploaded = await tryUploadAsset(file, blobClient, t('logo'));
      return uploaded ? getAssetUrl(sasTokenResponse) : null;
    } catch {
      // SAS-token request failures are already surfaced by the mutation's onError toast.
      return null;
    }
  };

  const handleSubmit = async (formValues: FormValues) => {
    let logoUrl = formValues.logoUrl;

    if (logoFile) {
      const uploadedLogoUrl = await uploadLogo(logoFile);
      if (!uploadedLogoUrl) {
        return;
      }
      logoUrl = uploadedLogoUrl;
    }

    const payload: OrganizationBranding = { organizationId, ...formValues, logoUrl };
    await updateBranding(payload).catch(() => undefined);
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values, setFieldValue, resetForm, isSubmitting, dirty }) => {
        const handleSelectLogo = (files: File[]) => {
          const file = files[0];
          if (!file) {
            return;
          }

          revokeLogoPreview();
          logoPreviewUrl.current = URL.createObjectURL(file);
          setLogoFile(file);
          setFieldValue('logoUrl', logoPreviewUrl.current);
        };

        const handleRemoveLogo = () => {
          revokeLogoPreview();
          setLogoFile(null);
          setFieldValue('logoUrl', DEFAULT_BRANDING.logoUrl);
        };

        const handleResetToDefault = async () => {
          try {
            await updateBranding({ organizationId, ...DEFAULT_BRANDING });
            revokeLogoPreview();
            setLogoFile(null);
            resetForm({ values: DEFAULT_BRANDING });
          } catch {
            // Failures are already surfaced by the mutation's onError toast.
          }
        };

        const hasCustomLogo = values.logoUrl !== DEFAULT_BRANDING.logoUrl;

        return (
          <Form className='mt-3 flex flex-col gap-9 md:mt-12 md:flex-row'>
            <div className='flex w-full flex-col gap-4 md:w-1/2'>
              <div className='flex flex-col gap-2'>
                <Label text={t('logo')} type={LABEL_TYPE.PARAGRAPH2} />
                {hasCustomLogo ? (
                  <div className='flex flex-col gap-2'>
                    <img
                      src={values.logoUrl}
                      alt={t('logo')}
                      className='h-16 w-auto rounded border border-secondary-blue-gray object-contain p-2'
                    />
                    <div className='md:w-1/2'>
                      <Button
                        variant={ButtonVariant.SECONDARY}
                        handleOnClick={handleRemoveLogo}
                        className='flex w-full items-center gap-2'>
                        <TrashIcon className='size-4 text-primary-blue' />
                        {t('removeLogo')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <UploadButton
                    multiple={false}
                    text='uploadLogo'
                    pickerTypes={UPLOAD_PICKER_TYPES.IMAGE}
                    showMaxFileSize={false}
                    isUploading={false}
                    onFilesSelected={handleSelectLogo}
                    variant={ButtonVariant.QUATERNARY}
                  />
                )}
              </div>

              {(() => {
                const renderColorField = (field: ColorFieldName, labelKey: string) => (
                  <ColorFieldPicker
                    key={field}
                    label={t(labelKey)}
                    value={values[field]}
                    isOpen={openPicker === field}
                    onToggle={(isOpen) => setOpenPicker(isOpen ? field : null)}
                    onChange={(value) => setFieldValue(field, value)}
                  />
                );

                return (
                  <>
                    <div className='flex flex-col gap-4 lg:flex-row'>
                      <div className='lg:w-1/2'>
                        <SingleSelect
                          id='fontFamily'
                          label={t('fontFamily')}
                          options={FONT_FAMILY_OPTIONS}
                          value={getFontFamilyOption(values.fontFamily)}
                          handleChange={(value) => {
                            const selected = (value as SelectOptionProps)?.value as string;
                            if (selected) {
                              setFieldValue('fontFamily', selected);
                            }
                          }}
                        />
                      </div>
                      <div className='lg:w-1/2'>
                        {renderColorField('headerBackgroundColor', 'headerBackgroundColor')}
                      </div>
                    </div>

                    <div className='flex flex-col gap-4 lg:flex-row'>
                      <div className='lg:w-1/2'>
                        {renderColorField('highlightColor', 'highlightColor')}
                      </div>
                      <div className='lg:w-1/2'>{renderColorField('linkColor', 'linkColor')}</div>
                    </div>

                    <div className='flex flex-col gap-4 lg:flex-row'>
                      <div className='lg:w-1/2'>
                        {renderColorField('footerColor', 'footerColor')}
                      </div>
                      <div className='lg:w-1/2'>
                        {renderColorField('footerBackgroundColor', 'footerBackgroundColor')}
                      </div>
                    </div>
                  </>
                );
              })()}

              <div className='mt-4 flex flex-col gap-4 md:flex-row'>
                <div className='md:w-1/3'>
                  <Button
                    variant={ButtonVariant.PRIMARY}
                    type='submit'
                    disabled={isSubmitting || isSaving || !dirty}
                    className='w-full'>
                    {t('save')}
                  </Button>
                </div>
                <div className='md:w-1/3'>
                  <Button
                    variant={ButtonVariant.SECONDARY}
                    handleOnClick={handleResetToDefault}
                    disabled={isSubmitting || isSaving || (isSavedAsDefault && !dirty)}
                    className='w-full'>
                    {t('resetToDefault')}
                  </Button>
                </div>
              </div>
            </div>

            <div className='flex w-full flex-col gap-2 md:w-1/2'>
              <Label text={t('emailPreview')} type={LABEL_TYPE.PARAGRAPH2} />
              <div
                style={{
                  fontFamily: values.fontFamily,
                  fontSize: 16,
                  lineHeight: 'normal',
                  backgroundColor: '#f6f7fb',
                  padding: 0,
                  margin: 0,
                  pointerEvents: 'none',
                  userSelect: 'none'
                }}>
                <div
                  style={{
                    maxWidth: 600,
                    margin: '0 auto',
                    backgroundColor: 'white',
                    border: '1px solid #ddd'
                  }}>
                  <div
                    style={{
                      backgroundColor: values.headerBackgroundColor,
                      padding: '20px 15px 15px 15px'
                    }}>
                    <img src={values.logoUrl} alt='Sentoa Logo' style={{ height: 30 }} />
                  </div>

                  <div style={{ padding: 30 }}>
                    <h2
                      style={{
                        color: values.highlightColor,
                        fontSize: 24,
                        fontWeight: 'bold',
                        marginTop: 0,
                        marginBottom: 20
                      }}>
                      {previewT('invitationEmailHeader')} {SAMPLE_USER_NAME},
                    </h2>

                    <p style={{ color: '#333', fontSize: 16, margin: '16px 0' }}>
                      {previewT('invitationEmailText')}
                    </p>

                    <p style={{ fontSize: 16, margin: '30px 0' }}>
                      {previewT('invitationUserEmailLink')}
                      <br />
                      <a
                        href={SAMPLE_INVITATION_URL}
                        style={{
                          color: values.linkColor,
                          textDecoration: 'none'
                        }}>
                        {SAMPLE_INVITATION_URL}
                      </a>
                    </p>

                    <hr
                      style={{ border: 'none', borderTop: '1px solid #e0e0e0', margin: '40px 0' }}
                    />

                    <p style={{ color: '#333', fontSize: 16, margin: '16px 0' }}>
                      {previewT('invitationEmailFooter')}
                    </p>
                  </div>

                  <div
                    style={{
                      textAlign: 'left',
                      fontSize: 12,
                      color: values.footerColor,
                      padding: 10,
                      backgroundColor: values.footerBackgroundColor
                    }}>
                    {previewT('automatedDoNotReplyMessage')}
                  </div>
                </div>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
